"use client";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useRouter } from "next/navigation";
import axios from "axios";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, AppState } from "@/store/configureStore";
import {
	VERIFY_USER_API,
	RESEND_OTP_WITH_EMAIL_API,
	RESEND_OTP_WITH_NUMBER_API,
	LOGOUT_API,
} from "@/constants";
import { clearUserInfo } from "@/store/slices/userSlice";
import { resetCart } from "@/store/slices/cartSlice";

const useValidateNewUser = () => {
	const user = useSelector((state: AppState) => state.user);
	const router = useRouter();
	const [tokens, setTokens] = useState<String[]>(["", "", "", "", "", ""]);
	const inputRefs = useRef<any[]>([]);
	const OTP = tokens.join("");
	const dispatch = useDispatch<AppDispatch>();

	const userVerificationSchema = yup.object({
		OTP: yup.string().required("OTP is required"),
	});

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(userVerificationSchema),
	});

	const handleVerification = async (e: React.FormEvent) => {
		e.preventDefault();
		try {
			const response = await axios.post(VERIFY_USER_API, {
				userId: user?.id,
				otp: `${OTP}`,
			});
			toast.remove();
			toast.success(response.data.msg);
			router.push("/login");
			// console.log("API Response", response);
		} catch (error: string | any) {
			toast.error(`An error occured: ${error.response.data.error}`);
			// console.log("API Error", error);
		}
	};

	const handleResendOTP = async () => {
		const endpoint =
			user?.email !== null ? RESEND_OTP_WITH_EMAIL_API : RESEND_OTP_WITH_NUMBER_API;
		try {
			const response = await axios.post(endpoint, {
				email: user?.email !== null ? user?.email : "",
				phoneNumber: user?.phoneNumber !== null ? user?.phoneNumber : "",
			});
			// console.log("API Response", response);
			toast.remove();
			toast.success(`${response?.data?.msg}`);
		} catch (error: string | any) {
			// console.log("API Error", error);
			toast.remove();
			toast.error(
				`An error occured trying resend OTP: ${error?.response?.data?.error}`
			);
		}
	};

	const handleInputChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		if (value.length === 1 && index < inputRefs.current.length - 1) {
			inputRefs.current[index + 1].focus();
		}
		const updatedTokens = [...tokens];
		updatedTokens[index] = value;
		setTokens(updatedTokens);
	};
	const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
		if (
			e.key === "Backspace" &&
			index > 0 &&
			tokens[index] === "" &&
			inputRefs.current[index - 1]
		) {
			inputRefs.current[index - 1].focus();
		}
	};
	const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		const pastedData = e.clipboardData.getData("text/plain").slice(0, 6);
		const newOtp = pastedData.split("").slice(0, 6);
		setTokens(newOtp);
		if (inputRefs.current[5]) {
			inputRefs.current[5].focus();
		}
	};
	useEffect(() => {
		inputRefs.current[0]?.focus();
	}, []);

	const handleLogout = async () => {
		try {
			const response = await axios.post(
				`${LOGOUT_API}`,
				{},
				{
					headers: {
						Authorization: `Bearer ${user?.token}`,
					},
				}
			);
			toast.remove();
			toast.success(response?.data?.msg);
			router.push("/");
			dispatch(clearUserInfo());
			dispatch(resetCart());
			// console.log("API Response", response);
		} catch (error: any) {
			// console.log("API Error", error);
			toast.remove();
			toast.error(
				`An error occured trying to logout: ${error.response.data.error}`
			);
		}
	};

	return {
		register,
		handleSubmit,
		errors,
		handleVerification,
		tokens,
		inputRefs,
		handleInputChange,
		handlePaste,
		handleKeyDown,
		handleResendOTP,
		handleLogout,
	};
};

export default useValidateNewUser;
